/**
 * <analytics-drill-parts> — a decomposition, and the proof that it is one.
 *
 * THE PARTS ARE SHOWN TO ADD UP, not asserted to. The whole is one full
 * `<schmancy-segmented-bar>`: the parts are its segments, so a decomposition
 * that failed to cover its figure would leave a visible gap in the track. The
 * bar renders its own legend, and each legend row's figure is filled through
 * the primitive's `figure-<id>` slot with a pressable one — which is why the
 * key can stay on rather than being turned off and re-rendered beside it.
 *
 * A REMAINDER IS A ROW, NEVER A GAP. Campaign rows never cover a day's visits —
 * a visit that named no campaign carries no source — so the surface adds the
 * remainder as its own named part rather than printing four segments that stop
 * short. The bar stays full and the thing the data cannot attribute is named
 * instead of implied.
 *
 * EVERY PART IS ITSELF A FIGURE. A part whose own answer this surface can give
 * carries the same dotted rule and opens it — which is what makes a drill a
 * chain rather than a single pop-up, and why the row itself is not pressable:
 * one gesture, one meaning, at every depth.
 */
import "@mhmo91/schmancy/chart";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { css, html } from "lit";
import { property } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { LocalizeMixin } from "../../i18n/localize.ts";
import "../analytics-figure.view.ts";
import { formatFigure } from "../analytics-figure.view.ts";
import type { PartsAnswer } from "./drill.state.ts";
import { phraseText } from "./drill.state.ts";

@schmancyElement("analytics-drill-parts")
export class AnalyticsDrillParts extends LocalizeMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: block;
			}
		`,
	];

	@property({ attribute: false }) answer!: PartsAnswer;

	override render() {
		const total = this.answer.parts.reduce((sum, part) => sum + part.value, 0);
		return html`
			<div class="flex flex-col gap-4">
				<schmancy-segmented-bar
					thickness="14"
					.segments=${this.answer.parts.map((part) => ({
						id: part.id,
						label: phraseText(part.label, this),
						value: part.value,
					}))}
					.valueFormatter=${(value: number) =>
						formatFigure(value, this.answer.unit, this.locale)}
					emptyLabel=${this.t("admin.analytics.drill.partsEmpty")}
				>
					<schmancy-typography slot="header" type="label" token="sm">
						${this.t("admin.analytics.drill.partsHeader", {
							count: this.answer.parts.length,
						})}
					</schmancy-typography>
					${repeat(
						this.answer.parts,
						(part) => part.id,
						(part) => html`
							<analytics-figure
								slot=${`figure-${part.id}`}
								size="inline"
								.label=${phraseText(part.label, this)}
								.value=${part.value}
								.unit=${this.answer.unit}
								.node=${part.open}
							></analytics-figure>
						`,
					)}
				</schmancy-segmented-bar>

				<schmancy-typography
					type="body"
					token="sm"
					class="text-surface-onVariant"
				>
					${phraseText(this.answer.claim, this)}
				</schmancy-typography>

				<div class="flex items-baseline justify-between gap-3">
					<schmancy-typography
						type="label"
						token="sm"
						class="text-surface-onVariant"
					>
						${this.t("admin.analytics.drill.openedFrom")}
					</schmancy-typography>
					<schmancy-typography type="title" token="sm" class="tabular-nums">
						${formatFigure(this.answer.whole, this.answer.unit, this.locale)}
					</schmancy-typography>
				</div>

				${when(
					Math.round(total) !== Math.round(this.answer.whole),
					() => html`
						<schmancy-typography
							type="label"
							token="sm"
							class="text-error-default"
						>
							${this.t("admin.analytics.drill.partsShort", {
								sum: formatFigure(total, this.answer.unit, this.locale),
							})}
						</schmancy-typography>
					`,
				)}
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"analytics-drill-parts": AnalyticsDrillParts;
	}
}
